/**
 * Auth Configuration using react-query-auth
 * Bulletproof React pattern for auth state management
 * Task: 11.2 - Phase 1: Auth/Provider Refactoring
 *
 * CRITICAL: Preserves async deadlock prevention from original AuthContext
 * Uses setTimeout deferral for role/permission fetching to prevent callback deadlock
 */

import { configureAuth } from 'react-query-auth';
import type { AuthError } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import type {
  UserActiveRole,
  UserPermission,
  RoleName,
  PermissionName,
  ResourceType,
  ActionType,
} from '@/types/rbac';
import type {
  AuthUser,
  UserType,
  LoginCredentials,
  RegisterCredentials,
} from '@/types/auth';
import {
  hasRole,
  hasAnyRole,
  hasPermission,
  hasResourcePermission,
  calculateHighestRoleLevel,
  isAdminUser,
} from './authorization';
import { isValidRoleType } from './role-validation';
import { logger } from '@/lib/logger';

// Re-export types for consumers
export type { AuthUser, UserType, LoginCredentials, RegisterCredentials };

/**
 * Fetch user roles from database
 */
export async function fetchUserRoles(userId: string): Promise<UserActiveRole[]> {
  logger.debug('[lib/auth] fetchUserRoles called', { userId });
  try {
    const { data: rolesData, error: rolesError } = await supabase
      .rpc('get_user_roles', { check_user_id: userId });

    logger.debug('[lib/auth] get_user_roles RPC response', {
      data: rolesData,
      error: rolesError,
      hasData: !!rolesData,
      dataLength: rolesData?.length || 0,
    });

    if (rolesError) {
      console.error('[lib/auth] RPC error details:', {
        message: rolesError.message,
        details: rolesError.details,
        hint: rolesError.hint,
        code: rolesError.code,
      });
      throw rolesError;
    }

// Process and validate the roles data
    const userRoles = (rolesData || [])
      .filter(role => role && typeof role === 'object' && 'role_type' in role) // Basic validation
      .map(role => ({
        ...role,
        role_type: role.role_type as 'platform' | 'employer' | 'learner'
      }))
      .filter(role => isValidRoleType(role.role_type)); // Runtime type checking

    logger.debug('[lib/auth] Processed user roles', { userRoles });
    return userRoles;
  } catch (error) {
    logger.error('[lib/auth] Error fetching user roles', error);
    return [];
  }
}

/**
 * Fetch user permissions from database
 */
export async function fetchUserPermissions(userId: string): Promise<UserPermission[]> {
  try {
    const { data: permissionsData, error: permissionsError } = await supabase
      .rpc('get_user_permissions', { check_user_id: userId });

    if (permissionsError) throw permissionsError;

    const userPermissions: UserPermission[] = permissionsData || [];
    return userPermissions;
  } catch (error) {
    logger.error('[lib/auth] Error fetching user permissions', error);
    return [];
  }
}

/**
 * Fetch user types from database
 * User types determine which app sections the user can access (learner, employer, platform)
 */
export async function fetchUserTypes(): Promise<UserType[]> {
  try {
    const { data, error } = await supabase.rpc('get_user_types');

    if (error) {
      logger.error('[lib/auth] Error fetching user types', error);
      throw error;
    }

    return (data || []) as UserType[];
  } catch (error) {
    logger.error('[lib/auth] Error fetching user types', error);
    return [];
  }
}

/**
 * Get current user with RBAC data
 * This is the core function that react-query-auth will use
 */
async function getUserFn(): Promise<AuthUser | null> {
  logger.debug('[lib/auth] getUserFn called');

  const { data: { session }, error } = await supabase.auth.getSession();

  if (error) {
    logger.error('[lib/auth] Error getting session', error);

    // Handle invalid refresh token gracefully (common when switching environments)
    // Instead of throwing, sign out to clear the invalid session and return null
    if (error.message?.includes('Refresh Token') || error.code === 'refresh_token_not_found') {
      console.warn('[lib/auth] Invalid refresh token detected, clearing session');
      await supabase.auth.signOut();
      return null;
    }

    throw error;
  }

  if (!session?.user) {
    logger.debug('[lib/auth] No authenticated session found');
    return null;
  }

  logger.debug('[lib/auth] Session found for user', { email: session.user.email });

  // Fetch RBAC data and user types atomically
  // CRITICAL: This runs synchronously in getUserFn
  // The async deadlock prevention happens in the auth state listener
  const [roles, permissions, userTypes] = await Promise.all([
    fetchUserRoles(session.user.id),
    fetchUserPermissions(session.user.id),
    fetchUserTypes(),
  ]);

  const highestRoleLevel = calculateHighestRoleLevel(roles);
  const isAdmin = isAdminUser(roles);

  logger.debug('[lib/auth] User loaded with RBAC data', {
    email: session.user.email,
    rolesCount: roles.length,
    permissionsCount: permissions.length,
    userTypesCount: userTypes.length,
    isAdmin,
    highestRoleLevel,
  });

  return {
    user: session.user,
    session,
    emailVerified: true,
    isAdmin,
    roles,
    permissions,
    userTypes,
    highestRoleLevel,
  };
}

/**
 * Login function
 */
async function loginFn(credentials: LoginCredentials): Promise<AuthUser> {
  logger.debug('[lib/auth] loginFn called', { email: credentials.email });

  const { data, error } = await supabase.auth.signInWithPassword({
    email: credentials.email,
    password: credentials.password,
  });

  if (error) {
    logger.error('[lib/auth] Login error', error);
    throw error;
  }

  if (!data.session?.user) {
    throw new Error('No user returned from login');
  }

  // Fetch RBAC data and user types for the logged-in user
  const [roles, permissions, userTypes] = await Promise.all([
    fetchUserRoles(data.session.user.id),
    fetchUserPermissions(data.session.user.id),
    fetchUserTypes(),
  ]);

  const highestRoleLevel = calculateHighestRoleLevel(roles);
  const isAdmin = isAdminUser(roles);

  return {
    user: data.session.user,
    session: data.session,
    emailVerified: true,
    isAdmin,
    roles,
    permissions,
    userTypes,
    highestRoleLevel,
  };
}

/**
 * Register function
 *
 * NOTE: When email confirmation is enabled (enable_confirmations = true in config.toml),
 * Supabase returns data.user but data.session will be null until email is confirmed.
 * This function handles both cases:
 * - Email confirmation disabled: Returns full AuthUser with session and RBAC data
 * - Email confirmation enabled: Returns AuthUser with user but null session (RBAC fetched later)
 */
async function registerFn(credentials: RegisterCredentials): Promise<AuthUser> {
  logger.debug('[lib/auth] registerFn called', { email: credentials.email, accountType: credentials.accountType });

  const { data, error } = await supabase.auth.signUp({
    email: credentials.email,
    password: credentials.password,
    options: {
      emailRedirectTo: `${window.location.origin}/auth/verify-email`,
      data: {
        name: credentials.name || '',
        accountType: credentials.accountType || 'learner', // Default to learner if not specified
      },
    },
  });

  if (error) {
    logger.error('[lib/auth] Registration error', error);
    throw error;
  }

  // Check for user (not session) - session may be null when email confirmation required
  if (!data.user) {
    throw new Error('No user returned from registration');
  }

  // When email confirmation is enabled, session will be null
  // Return minimal AuthUser - RBAC data will be fetched after email confirmation
  if (!data.session) {
    logger.debug('[lib/auth] Registration successful, email confirmation required', { email: credentials.email });
    return {
      user: data.user,
      session: null,
      emailVerified: false,
      isAdmin: false,
      roles: [],
      permissions: [],
      userTypes: [],
      highestRoleLevel: 0,
    };
  }

  // Session exists (email confirmation disabled) - fetch RBAC data and user types
  const [roles, permissions, userTypes] = await Promise.all([
    fetchUserRoles(data.session.user.id),
    fetchUserPermissions(data.session.user.id),
    fetchUserTypes(),
  ]);

  const highestRoleLevel = calculateHighestRoleLevel(roles);
  const isAdmin = isAdminUser(roles);

  return {
    user: data.session.user,
    session: data.session,
    emailVerified: true,
    isAdmin,
    roles,
    permissions,
    userTypes,
    highestRoleLevel,
  };
}

/**
 * Logout function
 */
async function logoutFn(): Promise<void> {
  logger.debug('[lib/auth] logoutFn called');
  const { error } = await supabase.auth.signOut();

  if (error) {
    logger.error('[lib/auth] Logout error', error);
    throw error;
  }
}

/**
 * Configure react-query-auth
 */
const authConfig = configureAuth({
  userFn: getUserFn,
  loginFn,
  registerFn,
  logoutFn,
});

/**
 * Export auth components and hooks
 */
export const { useUser, useLogin, useRegister, useLogout, AuthLoader } = authConfig;

/**
 * Additional auth utility functions
 */

/**
 * Reset password function
 */
export async function resetPassword(email: string): Promise<{ error: AuthError | null }> {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`,
  });
  return { error };
}

/**
 * Resend verification email
 */
export async function resendVerification(email: string): Promise<{ error: AuthError | null }> {
  const { error } = await supabase.auth.resend({
    type: 'signup',
    email: email,
    options: {
      emailRedirectTo: `${window.location.origin}/auth/verify-email`,
    },
  });
  return { error };
}

/**
 * Update user profile
 */
export async function updateProfile(data: {
  name?: string;
  email?: string;
}): Promise<{ error: AuthError | null }> {
  const { error } = await supabase.auth.updateUser(data);
  return { error };
}

/**
 * Check if current user can manage another user
 */
export async function canManageUser(
  currentUserId: string | undefined,
  targetUserId: string
): Promise<boolean> {
  if (!currentUserId) return false;
  if (currentUserId === targetUserId) return true; // Can always manage yourself

  try {
    const { data, error } = await supabase.rpc('can_manage_user', {
      target_user_id: targetUserId,
      check_user_id: currentUserId,
    });

    if (error) throw error;
    return data === true;
  } catch (error) {
    logger.error('[lib/auth] Error checking user management permission', error);
    return false;
  }
}

/**
 * Refresh user roles and permissions
 */
export async function refreshUserRoles(userId: string): Promise<{
  roles: UserActiveRole[];
  permissions: UserPermission[];
}> {
  const [roles, permissions] = await Promise.all([
    fetchUserRoles(userId),
    fetchUserPermissions(userId),
  ]);

  return { roles, permissions };
}

/**
 * Sign in with OAuth provider (Google or Azure)
 * Initiates the OAuth flow by redirecting to the provider's auth page
 *
 * @param provider - The OAuth provider to use ('google' or 'azure')
 * @param redirectTo - Optional path to redirect to after successful authentication
 * @returns Object with url (redirect URL) or error
 */
export async function signInWithOAuth(
  provider: 'google' | 'azure',
  redirectTo?: string
): Promise<{ url: string | null; error: AuthError | null }> {
  // Build the callback URL
  let callbackUrl = `${window.location.origin}/auth/callback`;

  // Add redirect destination as query parameter if provided
  if (redirectTo) {
    callbackUrl += `?next=${encodeURIComponent(redirectTo)}`;
  }

  // Configure provider-specific options
  const options: {
    redirectTo: string;
    scopes?: string;
  } = {
    redirectTo: callbackUrl,
  };

  // Azure requires specific scopes for proper profile and offline access
  if (provider === 'azure') {
    options.scopes = 'email profile openid offline_access';
  }

  const { data, error } = await supabase.auth.signInWithOAuth({
    provider,
    options,
  });

  if (error) {
    logger.error('[lib/auth] OAuth sign-in error', error);
    return { url: null, error };
  }

  return { url: data.url, error: null };
}

/**
 * Exchange OAuth authorization code for a session
 * Handles PKCE verification automatically via Supabase
 * @param code - The authorization code from OAuth provider callback
 * @returns Object containing error if exchange failed
 */
export async function exchangeCodeForSession(
  code: string
): Promise<{ error: AuthError | null }> {
  const { error } = await supabase.auth.exchangeCodeForSession(code);

  if (error) {
    logger.error('[lib/auth] Code exchange error', error);
  }

  return { error };
}

/**
 * RBAC helper hooks that work with AuthUser
 * These can be used in components that need RBAC checks
 */

/**
 * Hook to check if user has a specific role
 */
export function useHasRole(roleName: RoleName): boolean {
  const user = useUser();
  if (!user.data?.roles) return false;
  return hasRole(user.data.roles, roleName);
}

/**
 * Hook to check if user has any of the specified roles
 */
export function useHasAnyRole(roleNames: RoleName[]): boolean {
  const user = useUser();
  if (!user.data?.roles) return false;
  return hasAnyRole(user.data.roles, roleNames);
}

/**
 * Hook to check if user has a specific permission
 */
export function useHasPermission(permissionName: PermissionName): boolean {
  const user = useUser();
  if (!user.data?.permissions) return false;
  return hasPermission(user.data.permissions, permissionName);
}

/**
 * Hook to check if user has resource permission
 */
export function useHasResourcePermission(resource: ResourceType, action: ActionType): boolean {
  const user = useUser();
  if (!user.data?.permissions) return false;
  return hasResourcePermission(user.data.permissions, resource, action);
}

/**
 * Convenience hook for accessing current user data
 * Consolidates useUser + useRole patterns into a single, flat API
 *
 * This hook provides:
 * - Direct access to user identity (id, email)
 * - Auth state (isAuthenticated, isLoading)
 * - RBAC data (roles, permissions, userTypes)
 * - Role convenience checks (isAdmin, isSuperAdmin, isManager, isMentor)
 * - User type convenience checks (isLearner, isEmployer, isPlatform)
 * - Role checking functions (hasRole, hasAnyRole)
 *
 * @example
 * const { id, email, isAdmin, userTypes, isLoading } = useCurrentUser();
 * if (isAdmin) { ... }
 * if (hasRole('mentor')) { ... }
 */
export function useCurrentUser() {
  const queryResult = useUser();
  const { data: auth, isLoading, isError, error, refetch } = queryResult;
  const roles = auth?.roles ?? [];

  // Compute highest role (by role_level)
  const highestRole = roles.length > 0
    ? roles.reduce((prev, current) =>
        current.role_level > prev.role_level ? current : prev
      )
    : null;

  return {
    // User identity
    id: auth?.user?.id ?? null,
    email: auth?.user?.email ?? null,

    // Auth state
    isAuthenticated: !!auth?.user && !!auth?.session,
    isLoading,
    isError,
    error,

    // RBAC data
    roles,
    permissions: auth?.permissions ?? [],
    userTypes: auth?.userTypes ?? [],
    highestRole,
    highestRoleLevel: auth?.highestRoleLevel ?? 0,

    // Role convenience checks
    isAdmin: auth?.isAdmin ?? false,
    isSuperAdmin: hasRole(roles, 'super_admin'),
    isManager: hasRole(roles, 'manager') || (auth?.isAdmin ?? false),
    isMentor: hasRole(roles, 'mentor'),

    // User type convenience checks
    isLearner: auth?.userTypes?.includes('learner') ?? false,
    isEmployer: auth?.userTypes?.includes('employer') ?? false,
    isPlatform: auth?.userTypes?.includes('platform') ?? false,

    // Role checking functions
    hasRole: (roleName: RoleName) => hasRole(roles, roleName),
    hasAnyRole: (roleNames: RoleName[]) => hasAnyRole(roles, roleNames),

    // Actions
    refreshAuth: refetch,

    // Full objects when needed
    user: auth?.user ?? null,
    session: auth?.session ?? null,
    auth,
  };
}
